import React, { useState } from "react";
import { MessageSquareReply, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

async function saveReply(id, text) {
  const r = await fetch(`${API}/api/admin/reviews/${id}/reply`, {
    method: "PATCH", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!r.ok) throw new Error("reply");
  return (await r.json()).admin_reply;
}

export default function AdminReply({ review, onSaved }) {
  const { t } = useLang();
  const [text, setText] = useState(review.admin_reply?.text || "");
  const [busy, setBusy] = useState(false);
  const dirty = text.trim() !== (review.admin_reply?.text || "");

  const submit = async (value) => {
    setBusy(true);
    try {
      const reply = await saveReply(review.id, value);
      setText(reply?.text || "");
      onSaved(review.id, reply);
      toast.success(t("admin_reply_saved"));
    } catch { toast.error(t("review_error")); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 rounded-xl bg-sand/50 border border-stoneLine p-3" data-testid={`admin-reply-${review.id}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-ochre font-semibold mb-1.5">
        <MessageSquareReply className="w-3.5 h-3.5" /> {t("admin_reply_label")}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={2} placeholder={t("admin_reply_placeholder")}
        className="w-full text-sm rounded-lg border border-stoneLine bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ochre/40" dir="auto" data-testid={`admin-reply-input-${review.id}`} />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[10px] text-muted-foreground">{text.length}/500</span>
        <div className="flex gap-1.5">
          {review.admin_reply && (
            <button onClick={() => submit("")} disabled={busy} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-stoneLine hover:bg-red-50 hover:text-red-700 transition-colors disabled:opacity-50" data-testid={`admin-reply-remove-${review.id}`}>
              <Trash2 className="w-3.5 h-3.5" /> {t("admin_reply_remove")}
            </button>
          )}
          <button onClick={() => submit(text)} disabled={busy || !dirty || text.trim().length < 2} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-ink text-sand hover:bg-ochre transition-colors disabled:opacity-50" data-testid={`admin-reply-save-${review.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquareReply className="w-3.5 h-3.5" />} {t("admin_reply_save")}
          </button>
        </div>
      </div>
    </div>
  );
}
